import React, {useState, useEffect, useCallback, useContext} from 'react'
import {useAudioPlayer, useAudioPosition} from 'react-use-audio-player'
import '../../../css/puzzles/lastStand/TheMachine.css'

import machineSFX from '../../../audio/audioFiles/sfx/machineSFX.mp3'

import {UnmountContextProvider} from '../../../context/UnmountContext'
import {BackgroundMusicContext} from '../../../context/BackgroundMusicContext'

import useTogglePopUp from '../../../hooks/useTogglePopUp'
import useErrorCodes from './theMachineHooks/useErrorCodes'
import usePhases from './theMachineHooks/usePhases'
import useTimer from './theMachineHooks/useTimer'

import PuzzlePopUp from '../PuzzlePopUp'

import MachineProgress from './MachineProgress'
import MachineDials from './MachineDials'
import MachineSliders from './MachineSliders'
import MachineBigBtn from './MachineBigBtn'
import MachineMultiBtn from './MachineMultiBtn'
import MachineSwitches from './MachineSwitches'
import MachineLightMatrix from './MachineLightMatrix'
import MachineTime from './MachineTime'
import EndingPopUp from './EndingPopUp'

function TheMachine({unmount, updateUnmount}) {
    const [puzzleSolved, setPuzzleSolved] = useState(false)
    const [playerInput, setPlayerInput] = useState({
        dials: '111',
        sliders: 'yy',
        multiBtn: '',
        switches: '000000',
        inject: ''
    })
    const [startInstruction] = useState('DANGER! SHUTDOWN!')
    const [instruction, setInstructions] = useState(startInstruction)
    
    const {fadeBackgroundAudio, loadTrack} = useContext(BackgroundMusicContext)
    const {isPopUpOpen, togglePopUp} = useTogglePopUp()
    const {phase, winningPhase, updatePhase} = usePhases()
    const {
        currentError,
        variation, 
        shuffleErrorCodes, 
        nextErrorCode,
        currentAnswer,
        updateCurrentAnswer
    } = useErrorCodes()
    
    const {
        timeRemaining,
        maxTime,
        overheating, 
        setOverheating, 
        overheatCountdown,
        startMachine,
        setStartMachine,
        resetGame,
        setResetGame
    } = useTimer()
    
    const [audioSprite] = useState({
        wrong: 0,
        correct: .25,
        steam: 1.8,
        shutdown: 3.25,
        alarm: 5.5
    })
    const {seek} = useAudioPosition()
    const {play, stop, playing} = useAudioPlayer({
        src: machineSFX,
        format: 'mp3',
        autoplay: false,
        onplay: () => { updateUnmount({name: 'safe', state: false}) },
        onend: () => updateUnmount({name: 'safe', state: true})
    })
    
    const updateInstructions = useCallback(() => {
        if (overheating && overheatCountdown > 0 && !playerInput.inject) {
            setInstructions(`OVERHEATING ${overheatCountdown}`)
        } else if (startMachine) {
            setInstructions(`ERROR: ${currentError.name}`)
            updateCurrentAnswer()
        } else {
            setInstructions(startInstruction)
        }
    }, [currentError, overheatCountdown, overheating, playerInput, startInstruction, startMachine, updateCurrentAnswer])
    
    function resetMachine() {
        !resetGame && setResetGame(true)
        !puzzleSolved && updatePhase('reset')
        shuffleErrorCodes()
        clearAnInput('multiBtn')
        clearAnInput('inject')
    }
    
    function winTheGame() {
        togglePopUp()
        resetMachine()
        fadeBackgroundAudio('OUT')
        loadTrack()
        seek(audioSprite.shutdown)
        play()
        setTimeout(() => stop(), 2250)
        updateUnmount({name: 'safe', state: true})
    }
    
    function clearAnInput(name) {
        setPlayerInput(prevInput => {
            return {
                ...prevInput,
                [name]: ''
            }
        })
    }
    
    function checkInput() {
        const {dials, sliders, multiBtn, switches, inject} = playerInput
        const player = dials + sliders + multiBtn + switches + inject
        if (player === currentAnswer.join('') && !overheating) {
            seek(audioSprite.correct)
            play()
            setTimeout(() => stop(), 1400)
            updatePhase()
            nextErrorCode()
            clearAnInput('multiBtn')
        } else if (overheating && inject){
            setOverheating(false)
            seek(audioSprite.steam)
            setTimeout(() => stop(), 1400)
            updateInstructions()
            clearAnInput('inject')
        } else if (!overheating && inject) {
            seek(audioSprite.wrong)
            play()
            setTimeout(() => stop(), 250)
            updatePhase('decrease')
            clearAnInput('inject')
        } else if (multiBtn && multiBtn !== currentAnswer[2]){
            seek(audioSprite.wrong)
            play()
            setTimeout(() => stop(), 250)
            updatePhase('decrease')
            clearAnInput('multiBtn')
        } 
    }
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { checkInput() }, [playerInput])
    
    useEffect(() => { 
        if (overheating) {
            if (overheatCountdown <= 0) {
                updatePhase('decrease')
                stop()
            }
            updateInstructions()
            !playing && seek(audioSprite.alarm) && play()
        } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overheatCountdown, audioSprite])
    
    useEffect(() => {
        if (startMachine) {
            if (instruction === startInstruction && !playing) {
                seek(audioSprite.wrong)
                play()
                setTimeout(() => stop(), 250)
            }
            setInstructions(prevInstruct => {
                return prevInstruct === startInstruction ? '!!MALFUNCTION!!' : 'PROCESSING...'
            })
            const timer = setTimeout(() => updateInstructions(), 2000)
            return () => clearTimeout(timer)
        } else {
            updateInstructions()
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentError, startMachine, startInstruction, audioSprite])
    
    useEffect(() => {
        resetGame && resetMachine()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetGame])
    
    useEffect(() => {
        if (phase >= winningPhase) {
            setPuzzleSolved(true)
            const timer = setTimeout(() => winTheGame(), 1500)
            return () => clearTimeout(timer)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, winningPhase])
        
    return (
        <div className="locationContainer locationContainer-theMachine">
            <MachineProgress
                instruction ={instruction}
                startMachine={startMachine}
                setStartMachine={setStartMachine}
                phase={phase}
                winningPhase={winningPhase}
            />
            <MachineDials setPlayerInput={setPlayerInput} resetGame={resetGame}/>
            <MachineSliders setPlayerInput={setPlayerInput} resetGame={resetGame}/>
            <MachineMultiBtn setPlayerInput={setPlayerInput} />
            <MachineSwitches
                switchImg={currentError.switchImg}
                switchIcon={currentError.switchIcon}
                variation={variation}
                setPlayerInput={setPlayerInput}
                overheating={overheating}
                resetGame={resetGame}
            />
            <MachineLightMatrix
                lightIndex={currentError.lightIndex}
                variation={variation}
                overheating={overheating}
                resetGame={resetGame}
            />
            <MachineTime timeRemaining={timeRemaining} maxTime={maxTime} />
            <MachineBigBtn
                injectNX6={playerInput.inject}
                setPlayerInput={setPlayerInput}
                overheating={overheating}
            />
            
            {isPopUpOpen &&
                <UnmountContextProvider>
                    <PuzzlePopUp
                        pageUnmount={unmount}
                        updatePageUnmount={updateUnmount}
                        location={'machine'}
                    >
                        <EndingPopUp finalPuzzle />
                    </PuzzlePopUp>
                </UnmountContextProvider>
            }
        </div>
    )
}

export default TheMachine